import { useEffect, useMemo, useState } from "react";
import { useOfflineMode } from "../../features/offline/state/OfflineModeProvider";

export type NetworkStatus = "online" | "offline" | "unknown";

export type EffectiveOfflineMode =
  | "online"
  | "offline-enabled"
  | "offline-disabled";

export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>(() => {
    if (typeof navigator === "undefined") return "unknown";
    return navigator.onLine ? "online" : "offline";
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleOnline = () => setStatus("online");
    const handleOffline = () => setStatus("offline");
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return status;
}

export function useEffectiveOfflineMode(): {
  status: EffectiveOfflineMode;
  network: NetworkStatus;
  offlineEnabled: boolean;
} {
  const network = useNetworkStatus();
  const { enabled: offlineEnabled } = useOfflineMode();

  const status = useMemo<EffectiveOfflineMode>(() => {
    if (offlineEnabled) {
      return "offline-enabled";
    }
    if (network === "online") {
      return "online";
    }
    return "offline-disabled";
  }, [network, offlineEnabled]);

  return { status, network, offlineEnabled };
}
